import { message, type UploadFile } from 'antd'
import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { DEVICE_RECORDS_CATEGORY_ID } from '../constants'
import type { DeviceRecord, UpdateDeviceRecordInput } from '../types'

interface UseDeviceRecordUpdateReturn {
  updateDeviceRecord: (
    input: UpdateDeviceRecordInput,
    fileList?: UploadFile[]
  ) => Promise<DeviceRecord>
  isUpdating: boolean
}

export const useDeviceRecordUpdate = (
  residentId: string,
  onSuccess?: () => void
): UseDeviceRecordUpdateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      input,
      fileList
    }: {
      input: UpdateDeviceRecordInput
      fileList?: UploadFile[]
    }) => {
      const { id, existingDocumentIds, ...updateData } = input

      const hasFiles = fileList && fileList.length > 0
      let documentIds: string[] | undefined

      if (hasFiles) {
        try {
          const existingIds = existingDocumentIds || []
          const newDocumentIds = await uploadDocuments(
            fileList,
            residentId,
            DEVICE_RECORDS_CATEGORY_ID
          )
          documentIds = [...existingIds, ...newDocumentIds]
        } catch (error) {
          throw new Error('Failed to upload documents', { cause: error })
        }
      }

      const response = await postJSON({
        url: `/v6/residents/device-records/${id}`,
        method: 'PATCH',
        json: {
          ...updateData,
          ...(documentIds !== undefined && { documents: documentIds })
        }
      })

      return response.data as DeviceRecord
    },
    {
      onSuccess: () => {
        message.success('Device record updated successfully')
        queryClient.invalidateQueries(['device-records', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update device record')
      }
    }
  )

  const updateDeviceRecord = async (
    input: UpdateDeviceRecordInput,
    fileList?: UploadFile[]
  ): Promise<DeviceRecord> => {
    return mutation.mutateAsync({ input, fileList })
  }

  return {
    updateDeviceRecord,
    isUpdating: mutation.isLoading
  }
}
